/**
 * Tasks across the workspace (Phase 5.3): yours, or everything in your rooms,
 * by status, with a project filter. Same task objects as the room cards.
 */
import { useMemo, useState } from "react";
import { byUrgency, TaskRow, useTaskList } from "../components/Tasks";
import { ErrorNote, Fold } from "../components/ui";
import { STATUS_LABEL, useStore, type TaskStatus } from "../lib/store";

const COLUMNS: TaskStatus[] = ["doing", "review", "todo", "done"];

export function TasksPage() {
  const me = useStore((s) => s.boot!.me.id);
  const projects = useStore((s) => s.boot!.projects);
  const { list, error } = useTaskList("/api/tasks");
  const [scope, setScope] = useState<"mine" | "made" | "all">("mine");
  const [project, setProject] = useState("");
  const shown = useMemo(
    () =>
      (list ?? []).filter(
        (t) =>
          (scope === "all" || (scope === "mine" ? t.ownerId === me : t.createdBy === me)) &&
          (!project || (project === "none" ? !t.projectId : t.projectId === project)),
      ),
    [list, scope, project, me],
  );
  return (
    <main className="page tasks-page">
      <h1>Tasks</h1>
      <div className="tabs" role="tablist">
        {(
          [
            ["mine", "Mine"],
            ["made", "Created by me"],
            ["all", "All my rooms"],
          ] as const
        ).map(([k, label]) => (
          <button key={k} role="tab" aria-selected={scope === k} className={scope === k ? "on" : ""} onClick={() => setScope(k)}>
            {label}
          </button>
        ))}
        <select value={project} onChange={(e) => setProject(e.target.value)} aria-label="Project" className="tabs-filter">
          <option value="">Every project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
          <option value="none">No project</option>
        </select>
      </div>
      <ErrorNote error={error} />
      {!list && !error && <p className="muted-text">Loading</p>}
      {list && !shown.length && <p className="muted-text">Nothing here. Create tasks in a room: its panel, New task, or a message's More menu.</p>}
      {COLUMNS.map((status) => {
        const items = shown
          .filter((t) => t.status === status)
          .sort(byUrgency)
          .slice(0, status === "done" ? 20 : undefined);
        if (!items.length) return null;
        return (
          <Fold
            key={status}
            id={`tasks-${status}`}
            title={
              <>
                {STATUS_LABEL[status]} <span className="muted-text">{items.length}</span>
              </>
            }
            summary={items.slice(0, 4).map((t) => t.title).join(", ") + (items.length > 4 ? ` and ${items.length - 4} more` : "")}
          >
            <ul className="task-list">
              {items.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </ul>
          </Fold>
        );
      })}
    </main>
  );
}
